<template>
    <Head :title="cycle?.is_self_assessment_only ? t('assessments.cycleDetail.pageTitleSelf') : t('assessments.cycleDetail.pageTitle360')" />
    <BaseLayout>
        <BaseLink href="/reflections" class="self-start body-small text-ink-muted hover:text-ink flex items-center gap-1">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("assessments.cycleDetail.backToReflections") }}
        </BaseLink>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("assessments.cycleDetail.loading") }}</div>
        <div v-else-if="loadError" class="flex flex-col items-start gap-3">
            <BaseNotification theme="error" :text="loadError" hide-dismiss class="self-stretch" />
            <BaseButton theme="neutral" size="sm" @click="loadCycle">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <template v-else-if="cycle">
            <BasePageHeader :title="cycle.template.name">
                <template #eyebrow>
                    <StatusChip :state="stateChipState">{{ stateLabel }}</StatusChip>
                </template>
                <template v-if="cycle.launched_at || cycle.closes_at" #subtitle>
                    <span v-if="cycle.launched_at">{{ t("assessments.cycleDetail.started", { date: formatDate(cycle.launched_at) }) }}</span>
                    <span v-if="cycle.closes_at"> {{ t("assessments.cycleDetail.closes", { date: formatDate(cycle.closes_at) }) }}</span>
                </template>
                <template v-if="cycle.unlocked_at != null" #actions>
                    <a :href="`/reflections/360/${cycle.id}/results`" class="base-btn-primary base-btn-sm self-start shrink-0" @click="onViewResultsClick">{{
                        t("assessments.cycleDetail.viewResults")
                    }}</a>
                </template>
            </BasePageHeader>

            <!-- Progress card (360 only) -->
            <BaseCard v-if="!cycle.is_self_assessment_only" padding="lg">
                <div class="flex items-baseline justify-between mb-3">
                    <div>
                        <div class="title-body text-ink">
                            {{
                                t(
                                    "assessments.cycleDetail.respondedOfTotal",
                                    { responded: cycle.response_summary.responded, total: cycle.response_summary.total },
                                    cycle.response_summary.total,
                                )
                            }}
                        </div>
                        <div class="body-small text-ink-muted mt-1">{{ unlockNote }}</div>
                    </div>
                    <div v-if="thresholds === null" class="text-right">
                        <div class="details uppercase tracking-wide text-ink-muted">{{ t("assessments.cycleDetail.minNeeded") }}</div>
                        <div class="title-body text-ink">{{ cycle.min_responses }}</div>
                    </div>
                </div>
                <BaseProgressBar
                    :value="progressPct"
                    size="sm"
                    :color="unlocked ? 'var(--color-status-success)' : undefined"
                    :aria-label="t('assessments.cycleDetail.progress', { percent: progressPct })" />
            </BaseCard>

            <!-- Self-assess CTA -->
            <BaseCard v-if="cycle.self_assess_enabled && cycle.state !== 'closed'" theme="assessment" padding="md" class="flex items-center justify-between gap-3">
                <div>
                    <div class="body-small-bold text-ink">{{ t("assessments.cycleDetail.selfAssessTitle") }}</div>
                    <div v-if="selfSubmitted" class="body-small text-ink-muted">
                        {{ t("assessments.cycleDetail.selfCompletedOn", { date: formatDate(cycle.self_response_submitted_at) }) }}
                    </div>
                    <div v-else-if="cycle.is_self_assessment_only" class="body-small text-ink-muted">{{ t("assessments.cycleDetail.selfOnlyPrompt") }}</div>
                    <div v-else class="body-small text-ink-muted">{{ t("assessments.cycleDetail.selfPrompt") }}</div>
                </div>
                <StatusChip v-if="selfSubmitted" state="done" class="shrink-0">{{ t("assessments.cycleDetail.selfComplete") }}</StatusChip>
                <a v-else :href="`/reflections/360/${cycle.id}/self`" class="base-btn-neutral base-btn-sm shrink-0">{{ t("assessments.cycleDetail.openSelfAssess") }}</a>
            </BaseCard>

            <!-- Rater list (360 only) -->
            <section v-if="!cycle.is_self_assessment_only">
                <div class="flex items-baseline justify-between mb-2">
                    <h2 class="body-small-bold uppercase tracking-wide text-ink-muted">{{ t("assessments.cycleDetail.invitedRaters") }}</h2>
                    <BaseLink v-if="canAddRaters" :href="`/reflections/360/${cycle.id}/nominate`" class="body-small-semibold text-ink flex items-center gap-1">
                        <i class="bi bi-plus-lg" aria-hidden="true"></i> {{ t("assessments.cycleDetail.addRaters") }}
                    </BaseLink>
                </div>
                <BaseCard padding="none">
                    <ul class="divide-y divide-border">
                        <li v-for="invite in cycle.invites" :key="invite.id" class="px-4 py-3 flex items-center gap-3">
                            <PeopleAvatar size="sm" :name="invite.rater.display_name" />
                            <div class="flex-1 min-w-0">
                                <div class="body-small-semibold text-ink">{{ invite.rater.display_name }}</div>
                                <div class="details text-ink-muted truncate">{{ invite.rater.email }}</div>
                                <div v-if="invite.last_reminder_at" class="details text-ink-muted mt-1">
                                    {{ t("assessments.cycleDetail.lastReminded", { date: formatInviteNotificationDate(invite.last_reminder_at) }) }}
                                </div>
                                <div v-else-if="invite.invited_at" class="details text-ink-muted mt-1">
                                    {{ t("assessments.cycleDetail.reviewRequested", { date: formatInviteNotificationDate(invite.invited_at) }) }}
                                </div>
                            </div>
                            <BaseButton
                                v-if="canActOnInvite(cycle, invite) && canAddRaters"
                                type="button"
                                theme="neutral"
                                size="sm"
                                :disabled="resendingId === invite.id || isResendDisabled(invite)"
                                :title="isResendDisabled(invite) ? t('assessments.cycleDetail.resendLimit') : t('assessments.cycleDetail.resendTitle')"
                                @click="resendInvite(invite)">
                                <i
                                    class="bi"
                                    :class="resendingId === invite.id ? 'bi-hourglass-split' : resentIds.has(invite.id) ? 'bi-check2' : 'bi-envelope'"
                                    aria-hidden="true"></i>
                                {{ resendButtonLabel(invite.id) }}
                            </BaseButton>
                            <AssessmentInviteCopyMenu v-if="invite.invite_token" :invite="invite" :cycle="cycle" />
                            <StatusChip v-if="invite.state" :state="inviteStateChipState(invite.state)">{{
                                t("assessments.cycleDetail.inviteState", { state: invite.state })
                            }}</StatusChip>
                        </li>
                    </ul>
                </BaseCard>
                <BaseNotification v-if="resendError" theme="error" :text="resendError" hide-dismiss class="mt-2" />
                <div v-if="!cycle.rater_status_hidden" class="mt-2 details text-ink-muted">
                    {{ t("assessments.cycleDetail.anonymityNote") }}
                </div>
            </section>

            <AssessmentDemoTools :cycle-actions="cycleDemoActions" @deleted="onCycleDeleted" />
        </template>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleForSubject } from "~vue/api/generated/reflections";
import AssessmentInviteCopyMenu from "~vue/components/AssessmentInviteCopyMenu.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useAssessmentInviteResend } from "~vue/composables/useAssessmentInviteResend";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { submittedProgressLabel, submittedProgressPct } from "~vue/utils/categoryThresholds";
import { logUserInteraction } from "~vue/utils/logUtils";
import { canActOnInvite, cycleStateChipState, cycleStateLabel, formatDate, inviteStateChipState, isNetworkError } from "~vue/utils/threeSixty";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    cycle_id: string;
}>();

const cycle = ref<CycleForSubject | null>(null);
const selfSubmitted = computed(() => Boolean(cycle.value?.self_response_submitted_at));
const loading = ref(true);
const loadError = ref<string | null>(null);
const resendError = ref<string | null>(null);

const unlocked = computed(() => cycle.value?.state === "unlocked");
const cycleDemoActions = computed(() => {
    const currentCycle = cycle.value;
    if (currentCycle === null) {
        return [];
    }
    const canFill = (currentCycle.state === "draft" || currentCycle.state === "collecting") && (currentCycle.is_self_assessment_only || currentCycle.response_summary.pending > 0);
    return [{ id: currentCycle.id, label: currentCycle.template.name, canFill }];
});

// The page is scoped to a cycle that no longer exists, so leave for the hub.
function onCycleDeleted(): void {
    router.visit("/reflections");
}

// The API reports expired collecting and unlocked cycles as CLOSED, so only
// collecting or unlocked cycles can accept more raters.
const canAddRaters = computed<boolean>(() => {
    if (cycle.value === null) {
        return false;
    }
    return cycle.value.state === "collecting" || cycle.value.state === "unlocked";
});

const thresholds = computed(() => cycle.value?.category_thresholds ?? null);

const unlockNote = computed(() => {
    if (thresholds.value !== null) {
        return t("assessments.cycleDetail.unlockNoteThresholds", { progress: submittedProgressLabel(thresholds.value) });
    }
    return t("assessments.cycleDetail.unlockNoteMinimum", { count: cycle.value?.min_responses ?? 0 });
});

const progressPct = computed(() => {
    if (!cycle.value) {
        return 0;
    }
    if (thresholds.value !== null) {
        return submittedProgressPct(thresholds.value);
    }
    const total = Math.max(cycle.value.response_summary.total, cycle.value.min_responses);
    return Math.min(100, Math.round((cycle.value.response_summary.responded / total) * 100));
});

const stateLabel = computed(() => cycleStateLabel(cycle.value?.state));
const stateChipState = computed(() => cycleStateChipState(cycle.value?.state));

const { resendingId, resentIds, resendInvite, isResendDisabled, formatInviteNotificationDate } = useAssessmentInviteResend({
    cycleId: props.cycle_id,
    setError: (message) => {
        resendError.value = message;
    },
    onInviteUpdated: (inviteId, updatedInvite) => {
        if (!cycle.value) {
            return;
        }
        const idx = cycle.value.invites.findIndex((invite) => invite.id === inviteId);
        if (idx !== -1) {
            cycle.value.invites[idx] = updatedInvite;
        }
    },
});

function resendButtonLabel(inviteId: string): string {
    if (resendingId.value === inviteId) {
        return t("assessments.cycleDetail.resendSending");
    }
    return resentIds.value.has(inviteId) ? t("assessments.cycleDetail.resendSent") : t("assessments.cycleDetail.resend");
}

// Named so the error state can run it again. The failure it most often has to
// recover from is transient, and a full page reload is a heavy way to retry one.
async function loadCycle() {
    loading.value = true;
    loadError.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}`).json<CycleEnvelopeResponse>();
    if (fetchError.value) {
        loading.value = false;
        loadError.value = isNetworkError(fetchError.value)
            ? t("assessments.cycleDetail.connectionLoadFailed")
            : fetchError.value.message || t("assessments.cycleDetail.loadFailed");
        return;
    }
    const loaded = data.value?.cycle;
    if (!loaded) {
        loading.value = false;
        loadError.value = t("assessments.cycleDetail.loadFailed");
        return;
    }
    // DRAFTs aren't displayable here — the page assumes the cycle has been
    // launched. Self-assessment draft cycles stay here (they auto-launch on
    // creation). 360 draft cycles bounce to nominate to finish setup.
    if (loaded.state === "draft" && !loaded.is_self_assessment_only) {
        globalThis.location.replace(`/reflections/360/${props.cycle_id}/nominate`);
        return;
    }
    // A completed self-assessment-only cycle has nothing actionable left on
    // this page (no raters to track, the self-assessment is done) — send the
    // subject straight to their results, where they can retake if they want.
    if (loaded.is_self_assessment_only && loaded.state === "unlocked") {
        globalThis.location.replace(`/reflections/360/${props.cycle_id}/results`);
        return;
    }
    loading.value = false;
    cycle.value = loaded;
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CYCLE_DETAIL_VIEWED, {
        cycle_id: props.cycle_id,
        state: loaded.state,
        responded: loaded.response_summary.responded,
        total: loaded.response_summary.total,
        min_responses: loaded.min_responses,
    });
}

onMounted(loadCycle);

function onViewResultsClick() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CYCLE_VIEW_RESULTS_CLICKED, {
        cycle_id: props.cycle_id,
    });
}
</script>
